// Messages from a Jookable site admin to the TVs: the shape the server sends
// (src/helpers/adminMessages.ts) and the small rules the TV uses to show them.

export const ADMIN_MESSAGE_MAX_LENGTH = 200;
export const ADMIN_MESSAGE_MIN_SECONDS = 3;
export const ADMIN_MESSAGE_MAX_SECONDS = 3600;

/** A message on the TV now. */
export interface LiveAdminMessage {
  id: number;
  message: string;
  /** Sent to every jukebox, rather than to this one. */
  everyone: boolean;
  /** How long the admin chose to show it for. */
  seconds: number;
  /** When it comes off the TV, in this browser's clock. */
  endsAt: number;
}

/**
 * The server's list, with each one's time left turned into this browser's clock. A message
 * this screen already shows keeps the end it had, so the countdown bar does not jump each
 * time the list is read again.
 */
export const adminMessagesFromServer = (
  rows: any[],
  previous: LiveAdminMessage[] = [],
  now: number = Date.now(),
): LiveAdminMessage[] => {
  const known = new Map(previous.map(message => [message.id, message]));
  return (Array.isArray(rows) ? rows : [])
    .filter(row => row && typeof row.message === 'string' && Number(row.msLeft) > 0)
    .map(row => {
      const before = known.get(row.id);
      return {
        id: row.id,
        message: row.message,
        everyone: !!row.everyone,
        seconds: Number(row.seconds) || 0,
        endsAt: before ? before.endsAt : now + Number(row.msLeft),
      };
    });
};

/** The one to show: the newest still showing. Older ones come back when it ends. */
export const currentAdminMessage = (messages: LiveAdminMessage[], now: number = Date.now()): LiveAdminMessage | null =>
  messages.filter(message => message.endsAt > now).sort((a, b) => b.id - a.id)[0] || null;

/** The next moment one comes off the TV, or null. */
export const nextAdminMessageEnd = (messages: LiveAdminMessage[], now: number = Date.now()): number | null => {
  const future = messages.map(message => message.endsAt).filter(moment => moment > now);
  return future.length > 0 ? Math.min(...future) : null;
};
